import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { getTasks, takeTask } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  MagnifyingGlass,
  Storefront,
  Clock,
  CurrencyDollar,
  ListChecks,
  Hand,
  SortAscending,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const COMPLEXITY_COLORS = {
  baja: 'bg-green-500/20 text-green-400',
  media: 'bg-yellow-500/20 text-yellow-400',
  alta: 'bg-orange-500/20 text-orange-400',
  experta: 'bg-purple-500/20 text-purple-400',
};

const COMPLEXITY_PRICES = {
  baja: 10,
  media: 25,
  alta: 50,
  experta: 90,
};

const SKILLS = ['frontend', 'backend', 'mobile', 'qa', 'design', 'devops', 'pm', 'soporte'];

export function MarketplacePage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [skillFilter, setSkillFilter] = useState('all');
  const [complexityFilter, setComplexityFilter] = useState('all');
  const [sortBy, setSortBy] = useState('price_desc');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchTasks = useCallback(async () => {
    try {
      const response = await getTasks({ is_marketplace: true, status: 'disponible' });
      setTasks(response.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleTakeTask = async (taskId) => {
    try {
      await takeTask(taskId);
      toast.success('Task taken successfully!');
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const filteredTasks = tasks
    .filter(task => {
      const matchesSearch = task.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesSkill = skillFilter === 'all' || task.required_skills?.includes(skillFilter);
      const matchesComplexity = complexityFilter === 'all' || task.complexity === complexityFilter;
      return matchesSearch && matchesSkill && matchesComplexity;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'price_desc':
          return (b.price || 0) - (a.price || 0);
        case 'price_asc':
          return (a.price || 0) - (b.price || 0);
        case 'deadline':
          return (a.deadline || '').localeCompare(b.deadline || '');
        case 'complexity':
          const order = { experta: 4, alta: 3, media: 2, baja: 1 };
          return (order[b.complexity] || 0) - (order[a.complexity] || 0);
        default:
          return 0;
      }
    });

  const totalValue = filteredTasks.reduce((sum, t) => sum + (t.price || 0), 0);

  return (
    <Layout title={t('marketplace')}>
      <div className="space-y-6" data-testid="marketplace-page">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="valhalla-card p-5 bg-gradient-to-br from-[#E2B05E]/20 to-[#E2B05E]/5 border-[#E2B05E]/30">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-[#0B0E14]/50 text-[#E2B05E]">
                <Storefront weight="duotone" className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-[#9CA3AF] uppercase tracking-wider">{t('availableTasks')}</p>
                <p className="font-['Outfit'] text-2xl font-bold text-[#F3F4F6]">{filteredTasks.length}</p>
              </div>
            </div>
          </div>
          <div className="valhalla-card p-5 bg-gradient-to-br from-[#3AB8D6]/20 to-[#3AB8D6]/5 border-[#3AB8D6]/30">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-[#0B0E14]/50 text-[#3AB8D6]">
                <CurrencyDollar weight="duotone" className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-[#9CA3AF] uppercase tracking-wider">Total Value</p>
                <p className="font-['Outfit'] text-2xl font-bold text-[#F3F4F6]">${totalValue.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="valhalla-card p-5 bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border-emerald-500/30">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-[#0B0E14]/50 text-emerald-400">
                <ListChecks weight="duotone" className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-[#9CA3AF] uppercase tracking-wider">Avg. Points</p>
                <p className="font-['Outfit'] text-2xl font-bold text-[#F3F4F6]">
                  {filteredTasks.length > 0 
                    ? (filteredTasks.reduce((sum, t) => sum + (t.story_points || 0), 0) / filteredTasks.length).toFixed(1)
                    : 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Complexity Pricing Guide */}
        <div className="valhalla-card p-4">
          <h3 className="text-sm font-medium text-[#9CA3AF] mb-3 uppercase tracking-wider">Pricing by Complexity</h3>
          <div className="flex flex-wrap gap-4">
            {Object.entries(COMPLEXITY_PRICES).map(([complexity, price]) => (
              <div key={complexity} className="flex items-center gap-2">
                <Badge className={COMPLEXITY_COLORS[complexity]}>{complexity}</Badge>
                <span className="text-[#E2B05E] font-bold">${price}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div className="flex items-center gap-3 flex-1 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
              <Input
                placeholder={t('search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 valhalla-input"
                data-testid="marketplace-search"
              />
            </div>
            <Select value={skillFilter} onValueChange={setSkillFilter}>
              <SelectTrigger className="w-36 valhalla-input">
                <SelectValue placeholder={t('filterBySkill')} />
              </SelectTrigger>
              <SelectContent className="bg-[#151B26] border-[#242E42]">
                <SelectItem value="all">{t('all')}</SelectItem>
                {SKILLS.map(skill => (
                  <SelectItem key={skill} value={skill}>{skill}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={complexityFilter} onValueChange={setComplexityFilter}>
              <SelectTrigger className="w-36 valhalla-input">
                <SelectValue placeholder={t('filterByComplexity')} />
              </SelectTrigger>
              <SelectContent className="bg-[#151B26] border-[#242E42]">
                <SelectItem value="all">{t('all')}</SelectItem>
                <SelectItem value="baja">{t('lowComplexity')}</SelectItem>
                <SelectItem value="media">{t('mediumComplexity')}</SelectItem>
                <SelectItem value="alta">{t('highComplexity')}</SelectItem>
                <SelectItem value="experta">{t('expert')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-44 valhalla-input">
              <SortAscending className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#151B26] border-[#242E42]">
              <SelectItem value="price_desc">Highest Price</SelectItem>
              <SelectItem value="price_asc">Lowest Price</SelectItem>
              <SelectItem value="deadline">Deadline</SelectItem>
              <SelectItem value="complexity">Complexity</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tasks Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTasks.map((task) => (
            <div key={task._id} className="valhalla-card p-5 flex flex-col" data-testid={`marketplace-task-${task._id}`}>
              <div className="flex-1">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-semibold text-[#F3F4F6]">{task.title}</h3>
                  <Badge className={COMPLEXITY_COLORS[task.complexity]}>{task.complexity}</Badge>
                </div>

                <p className="text-sm text-[#9CA3AF] line-clamp-2 mb-4">{task.description || 'No description'}</p>

                <div className="flex items-center gap-4 text-sm text-[#9CA3AF] mb-4">
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>{task.estimated_hours || 0}h</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <ListChecks className="w-4 h-4" />
                    <span>{task.story_points || 0} pts</span>
                  </div>
                </div>

                {task.required_skills?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-4">
                    {task.required_skills.map(skill => (
                      <Badge key={skill} variant="outline" className="text-xs border-[#242E42] text-[#9CA3AF]">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                )}

                {task.deadline && (
                  <p className="text-xs text-[#9CA3AF] mb-4">
                    Deadline: <span className="text-[#F3F4F6]">{task.deadline}</span>
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-[#242E42]">
                <div className="flex items-center gap-1">
                  <CurrencyDollar className="w-5 h-5 text-[#E2B05E]" />
                  <span className="font-['Outfit'] text-2xl font-bold text-[#E2B05E]">${task.price || COMPLEXITY_PRICES[task.complexity] || 0}</span>
                </div>
                <Button
                  onClick={() => handleTakeTask(task._id)}
                  className="bg-[#3AB8D6] hover:bg-[#3AB8D6]/80 text-white"
                  data-testid={`take-task-${task._id}`}
                >
                  <Hand className="w-4 h-4 mr-2" />
                  {t('takeTask')}
                </Button>
              </div>
            </div>
          ))}
        </div>

        {filteredTasks.length === 0 && !loading && (
          <div className="valhalla-card p-12 text-center">
            <Storefront className="w-12 h-12 mx-auto text-[#9CA3AF] mb-4" />
            <p className="text-[#9CA3AF]">{t('noData')}</p>
            <p className="text-sm text-[#9CA3AF] mt-1">No available tasks in the marketplace</p>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default MarketplacePage;
